import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Modal from '../../../../../../CustomComponents/Modal/index.js';
import { ACH_DISCLAIMER_TYPES } from '../../../../../../../helpers/utils/TransferMoneyUtils.js';

const DISCLAIMER_CONTENT = {
    [ACH_DISCLAIMER_TYPES.achOneTime]: {
        label: 'ach_disclaimer_one_time_label',
        summary: 'ach_disclaimer_one_time_summary',
        modalTitle: 'ach_disclaimer_one_time_modal_title',
        modalBody: 'ach_disclaimer_one_time_modal_body'
    },
    [ACH_DISCLAIMER_TYPES.achRecurring]: {
        label: 'ach_disclaimer_recurring_label',
        summary: 'ach_disclaimer_recurring_summary',
        modalTitle: 'ach_disclaimer_recurring_modal_title',
        modalBody: 'ach_disclaimer_recurring_modal_body'
    }
};

const BODY_BASE_CLASSES = 'font-hanken-grotesk text-[16px] leading-[24px]';

/**
 * Renders the NACHA/ACH transfer disclaimer on the Confirm Transfer screen.
 * Nothing is rendered for internal transfers.
 */
const ACHTransferLegalDisclaimer = ({ disclaimerType }) => {
    const { t } = useTranslation();
    const kudzuPlusTrue = useSelector(state => state.kudzuPlusTrue);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const titleTextColor = kudzuPlusTrue ? 'text-[#FFFFFF]' : 'text-[#262B3A]';
    const bodyTextColor = kudzuPlusTrue ? 'text-[#E2E4E9]' : 'text-[#262B3A]';
    const boxTextColor = kudzuPlusTrue ? 'text-[#FFFFFF]' : 'text-[#262B3A]';
    const linkTextColor = kudzuPlusTrue ? 'text-[#94C3E8]' : 'text-[#2965FB]';
    const BODY_TEXT_CLASSES = `${BODY_BASE_CLASSES} font-[400] ${bodyTextColor}`;
    const BODY_HEADING_CLASSES = `${BODY_BASE_CLASSES} font-[700] ${titleTextColor}`;
    const BODY_LINK_CLASSES = `${BODY_BASE_CLASSES} font-[700] ${linkTextColor}`;

    if (!disclaimerType || disclaimerType === ACH_DISCLAIMER_TYPES.internal) return null;

    const content = DISCLAIMER_CONTENT[disclaimerType];
    if (!content) return null;

    const isRecurring = disclaimerType === ACH_DISCLAIMER_TYPES.achRecurring;

    const modalBody = isRecurring ? (
        <>
            <p className={BODY_TEXT_CLASSES}>{t(content.modalBody)}</p>

            <h3 className={`${BODY_HEADING_CLASSES} pt-6`}>{t('ach_disclaimer_contact_us_heading')}</h3>
            <p className={BODY_TEXT_CLASSES}>
                {t('ach_disclaimer_contact_us_call')}{' '}
                <a href={`tel:${t('customerNo')}`} className={BODY_LINK_CLASSES}>{t('customerNo')}</a>
                {t('ach_disclaimer_contact_us_email')}{' '}
                <a href={`mailto:${t('supportEmail')}`} className={BODY_LINK_CLASSES}>{t('supportEmail')}</a>
                {t('ach_disclaimer_contact_us_secure_message')}
            </p>

            <h3 className={`${BODY_HEADING_CLASSES} pt-6`}>{t('ach_disclaimer_what_to_include_heading')}</h3>
            <p className={BODY_TEXT_CLASSES}>{t('ach_disclaimer_what_to_include_body')}</p>

            <p className={`${BODY_TEXT_CLASSES} pt-6`}>{t('ach_disclaimer_written_confirmation')}</p>
        </>
    ) : (
        <p className={BODY_TEXT_CLASSES}>{t(content.modalBody)}</p>
    );

    return (
        <>
            <div className={`mt-8 mb-2 px-5 py-4 rounded-lg ${kudzuPlusTrue ? 'bg-[#313A52]' : 'bg-[#F7F8F9]'}`}>
                <p className={`font-hanken-grotesk uppercase font-[600] tracking-[2px]
                    lg:text-[13px] text-[12px]
                    ${kudzuPlusTrue ? 'text-[#C2C6D0]' : 'text-[var(--confirm-row-title)] opacity-[70%]'}`}>{t(content.label)}</p>
                <p className={`font-hanken-grotesk font-[400] pt-2
                    text-[14px] leading-[20px] md:text-[16px] md:leading-[24px]
                    ${boxTextColor}`}>
                    {t(content.summary)}{' '}
                    <button type='button'
                        className={`font-hanken-grotesk font-[700] underline cursor-pointer ${linkTextColor}`}
                        onClick={() => setIsModalOpen(true)}>{t('read_more')}</button>
                </p>
            </div>

            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                isLogo={false}
                bgColor={kudzuPlusTrue ? 'bg-[#262B3A]' : undefined}
                modalbg={!kudzuPlusTrue}
                defaultCornerRadius={false}
                title={<h2 className={`font-serif-pro font-[400] lg:text-[36px] md:text-[32px] text-[28px]
                    leading-[40px] ${titleTextColor}`}>{t(content.modalTitle)}</h2>}
                content={
                    <div className='pt-6'>
                        {modalBody}
                        <div className='flex justify-end pt-14'>
                            <button type='button'
                                className={`font-hanken-grotesk font-[700] text-[18px] leading-[24px] cursor-pointer ${kudzuPlusTrue ? 'text-[#FFFFFF]' : 'text-[#2965FB]'}`}
                                onClick={() => setIsModalOpen(false)}>{t('got_it')}</button>
                        </div>
                    </div>
                }
            />
        </>
    );
};

export default ACHTransferLegalDisclaimer;
